import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../store/auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const { setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(decodeURIComponent(errorParam));
        return;
      }

      if (!token) {
        setError('No authentication token received');
        return;
      }

      try {
        // Get user data using the token
        const response = await fetch(`${API_URL}/user/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        interface UserResponse {
          success: boolean;
          data?: any;
          error?: string;
        }
        const data = await response.json() as UserResponse;

        if (data.success && data.data) {
          // Set user in store with the token from OAuth
          setUser(data.data, token);

          // Use window.location for a full page navigation to ensure
          // the persisted auth state is properly loaded
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 100);
        } else {
          setError(data.error || 'Authentication failed');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Failed to complete authentication');
      }
    };

    handleCallback();
  }, [searchParams, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-accent-red/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-accent-red" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Authentication Failed</h1>
          <p className="text-text-secondary mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="btn-primary"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mx-auto mb-6">
          <img
            src="/logos/logo-5.png"
            alt="SweetPilot"
            className="w-48 object-contain"
          />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Signing you in...</h1>
        <p className="text-text-secondary mb-6">Please wait while we complete your authentication.</p>
        <Loader2 className="w-8 h-8 text-accent-purple animate-spin mx-auto" />
      </div>
    </div>
  );
}
